import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  MenuItem,
  ListItemIcon,
  Hidden,
  FormControl,
  Select,
  Box,
} from "@mui/material";

//... Icon imports
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import MenuIcon from "@mui/icons-material/Menu";
import ClearIcon from "@mui/icons-material/Clear";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications";
import GroupAddIcon from "@mui/icons-material/GroupAdd";

//... Logo imports
import NBA_LOGO from "../../images/nba_logo.png";
import EliteCampIcon from "../../images/EliteCamp-min.png";
import EliteCombineIcon from "../../images/GL_Combine-min.png";
import BALIcon from "../../images/BAL-min.png";
import DraftCombineIcon from "../../images/DraftCombine.png";
import PortsMouthIcon from "../../images/PortsMouth.png";

//... Context imports
import { AppDataContext } from "../../common/appContext";

//... Helper and Constant imports
import Helper from "../../common/Helper";
import { Role, history } from "../../helper";
import RouteConstants from "../../common/RouteConstants";
import AppConstants from "../../common/AppConstants";

//... Style imports
import "./AppHeaderbar.css";

//... Service imports
import { AdminPreferencesService } from "../../services/adminPref.Service";
import { UserPreferenceService } from "../../services/userPref.Service";
import { GameAuditBlobsService } from "../../services/GameAudit.Service";

//... Local components
import NotificationPopover from "./NotificationPopover";
import AppSidebar from "./AppSidebar";

const useStyles = makeStyles((theme) => ({
  appBar: {
    zIndex: theme.zIndex.appBar - 1,
    color: theme.palette.type === "dark" && "#fff",
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.primary.dark
        : theme.palette.primary.main,
    [theme.breakpoints.down("md")]: {
      // height: "60px",
      height: "auto",
      fontSize: ".4rem",
    },
  },
  headerBar: {
    display: "flex",
    alignItems: "center",
    flex: "1",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      alignItems: "center",
      //flexDirection: "column",
    },
  },
  list: {
    "&:focus": {
      outline: "none",
    },
  },
  title: {
    // flexGrow: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    [theme.breakpoints.down("md")]: {
      lineHeight: 1,
    },
  },
  menu: {
    width: "250px",
    [theme.breakpoints.down("sm")]: {
      width: "165px",
    },
  },
  itemIcon: {
    minWidth: "35px",
  },
  avatar: {
    backgroundColor: theme.palette.primary.dark,
  },
  version: {
    paddingLeft: theme.spacing(2),
  },
  initails: {
    color: theme.palette.primary.main,
    backgroundColor: "#fff",
    width: "clamp(20px, 2vw + 20px, 40px)",
    height: "clamp(20px, 2vw + 20px, 40px)",
  },
  clearIcon: {
    top: "5px",
    left: "-5px",
  },
  menuIcon: {
    color: "#ffffff",
    left: "-17px",
  },
  subHeader: {
    background: "#fff",
  },
  navigationLink: {
    color: "#272727",
  },
  eventBlockContainer: {
    width: "250px",
    "& .MuiInputBase-root": {
      width: "250px",
      [theme.breakpoints.down("sm")]: {
        width: "150px",
      },
    },
    [theme.breakpoints.down("sm")]: {
      width: "150px",
    },
  },
  eventTitleWrapper: {
    display: "flex",
    "& img": {
      width: "24px",
      [theme.breakpoints.down("sm")]: {
        width: "15px",
      },
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.7rem",
    },
  },
}));

export default function AppHeaderbar(props) {
  const classes = useStyles();
  let navigate = useNavigate();

  //... Local States
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [eventname, setEventname] = useState([]);
  const open = Boolean(anchorEl);
  const [showVideoDashboardScreen, setShowVideoDashboardScreen] =
    useState(null);
  const [isWebView, setIsWebView] = useState(false);
  const [appSideBarOpen, setAppSideBarOpen] = useState(false);

  //...Filter state holders
  const [combineYearDropdownData, setCombineYearDropdownData] = useState([]);
  const [eventDropdownData, setEventDropdownData] = useState([
    sessionStorage.getItem("eventid") != null
      ? sessionStorage.getItem("eventid")
      : AppConstants.DRAFTCOMBINE_EVENT_ID,
  ]);

  const {
    user,
    originalUser,
    setSelectedEvent,
    endUserPersonate,
    combineYear,
    setCombineYear,
    selectedEventId,
    setLengthUnits,
    setWeightUnits,
    setCommonEventList,
  } = useContext(AppDataContext);

  let userAppRoles = user.appRoles || [];

  useEffect(() => {
    window.onload = () => {
      setIsWebView(Helper.detectWebView());
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const [yearsRes, flagRes] = await Promise.all([
        AdminPreferencesService.getCombineYears(),
        GameAuditBlobsService.getVideoDashboardDisplayFlag(),
      ]);

      const { data: years } = yearsRes;
      if (years?.length) {
        setCombineYear(Number(years[0].eventYear));
        setCombineYearDropdownData(years.map(({ eventYear }) => eventYear));
      }

      const { data: flag } = flagRes;
      if (flag?.length) {
        setShowVideoDashboardScreen(flag[0].childName === "true");
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    fetchEventname(combineYear);
  }, [combineYear]);

  //... Handlers
  const toggleAppSideBar = (open) => () => {
    setAppSideBarOpen(open);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleManageUsersClick = () => {
    navigate(RouteConstants.MANAGE_USERS);
    setAnchorEl(null);
  };

  const navigateToHomeScreen = () => {
    getUserUnitPreferencesByUserID(user.userID);
    navigate(RouteConstants.HOME);
    setAnchorEl(null);
  };

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/logout");
  };

  const handleManageUsersPreferencesClick = () => {
    navigate(RouteConstants.USER_PREFERENCES);
    setAnchorEl(null);
  };

  const handleRevisedGameAuditClick = () => {
    navigate(RouteConstants.REVISED_GAME_AUDIT);
    setAnchorEl(null);
  };

  const handleManageNotification = () => {
    navigate(RouteConstants.MANAGE_EMAIL_NOTIFICATION);
    setAnchorEl(null);
  };

  const navigateTeamPlayersClick = () => {
    navigate(RouteConstants.MANAGE_TEAM_PLAYERS);
    setAnchorEl(null);
  };

  const navigateFiveOnFiveGamesClick = () => {
    navigate(RouteConstants.FIVE_ON_FIVE_MANAGEMENT);
    setAnchorEl(null);
  };

  const navigateEventCalendarClick = () => {
    navigate(RouteConstants.EVENT_SCHEDULER);
    setAnchorEl(null);
  };

  //... Utility functions
  const getEventIcon = (leagueId, isAlt21 = false) => {
    const id = Number(leagueId);

    const icons = {
      11: BALIcon,
      9: DraftCombineIcon,
      23: PortsMouthIcon,
    };

    if (id === 21) {
      return isAlt21 ? EliteCombineIcon : EliteCampIcon;
    }

    return icons[id] || DraftCombineIcon;
  };

  function storeEventId(eid) {
    sessionStorage.removeItem("eventid");
    sessionStorage.setItem("eventid", eid);
  }

  //... Service calls
  async function getUserUnitPreferencesByUserID(iUserId) {
    UserPreferenceService.getUserUnitPreference(iUserId)
      .then((res) => {
        if (res) {
          setLengthUnits(res?.data?.length);
          setWeightUnits(res?.data?.weight);
        }
      })
      .catch((error) => console.log("getUserUnitPreferences", error));
  }

  const fetchEventname = async (yearEvent) => {
    if (!yearEvent) return;

    const { data } = await AdminPreferencesService.getEventName(yearEvent);
    setEventname(data);
    setCommonEventList(data);
  };

  return (
    <>
      {!isWebView && (
        <AppBar position="static" className={classes.appBar}>
          <Toolbar>
            <Box className={classes.headerBar}>
              <Box display="flex" flex="1">
                {Object.keys(user).length > 0 && (
                  <IconButton
                    className={classes.menuIcon}
                    onClick={toggleAppSideBar(true)}
                  >
                    <MenuIcon />
                  </IconButton>
                )}

                <Hidden smDown>
                  <img
                    alt="logo"
                    src={NBA_LOGO}
                    className="appbar-nba-logo appbar-nba-logo-rightMargin"
                    onClick={navigateToHomeScreen}
                    style={{ cursor: "pointer" }}
                    // onClick={
                    //   user &&
                    //   user.appRoles &&
                    //   ((user.appRoles.includes(Role.CombineBallotUser) &&
                    //     user.teamBallotDashboardFlag) ||
                    //     user.appRoles.includes(Role.DraftGreenRoomBallotUser))
                    //     ? null
                    //     : navigateToHomeScreen
                    // }
                    // style={{
                    //   cursor:
                    //     user &&
                    //     user.appRoles &&
                    //     ((user.appRoles.includes(Role.CombineBallotUser) &&
                    //       user.teamBallotDashboardFlag) ||
                    //       user.appRoles.includes(Role.DraftGreenRoomBallotUser))
                    //       ? "default"
                    //       : "pointer",
                    // }}
                  />
                </Hidden>
                <Hidden smDown>
                  <Typography
                    justifyContent="flex-start"
                    variant="body2"
                    className={`${classes.title} headerTitle`}
                  >
                    <span
                      onClick={navigateToHomeScreen}
                      style={{ cursor: "pointer" }}
                      // onClick={
                      //   user &&
                      //   user.appRoles &&
                      //   ((user.appRoles.includes(Role.CombineBallotUser) &&
                      //     user.teamBallotDashboardFlag)
                      //     ||
                      //     user.appRoles.includes(Role.DraftGreenRoomBallotUser)
                      //     )
                      //     ? null
                      //     : navigateToHomeScreen
                      // }
                      // style={{
                      //   cursor:
                      //     user &&
                      //     user.appRoles &&
                      //     ((user.appRoles.includes(Role.CombineBallotUser) &&
                      //       user.teamBallotDashboardFlag) ||
                      //       user.appRoles.includes(Role.DraftGreenRoomBallotUser))
                      //       ? "default"
                      //       : "pointer",
                      // }}
                    >
                      Combine Central
                    </span>
                  </Typography>
                </Hidden>
              </Box>

              {user && (
                <Box display="flex">
                  <Box className={classes.eventBlockContainer} mr={2}>
                    <FormControl size="small" fullWidth>
                      <Select
                        size="small"
                        value={eventDropdownData}
                        onChange={(e) => {
                          setEventDropdownData(e.target.value + "");
                          setSelectedEvent(e.target.value + "");
                          storeEventId(e.target.value + "");
                        }}
                        sx={{
                          backgroundColor: "#fff",
                          color: "#1d428a",
                          border: "none",
                          fontSize: "0.9rem",
                          width: "250px",
                        }}
                        inputProps={{ "aria-label": "Without label" }}
                        displayEmpty
                      >
                        {eventname.map((elem, i) => {
                          const isAlt21 =
                            elem.leagueId === "21" && elem.eventYear === 2026;

                          return (
                            <MenuItem
                              key={i}
                              value={elem.leagueId}
                              alignItems="center"
                            >
                              <span
                                title={elem.eventName}
                                className={classes.eventTitleWrapper}
                              >
                                <img
                                  src={getEventIcon(elem.leagueId, isAlt21)}
                                  alt={elem.eventName}
                                  style={{ paddingRight: "5px" }}
                                />{" "}
                                {elem.eventName}
                              </span>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box style={{ width: "80px" }}>
                    <FormControl
                      size="small"
                      fullWidth
                      sx={{ maxWidth: "80px !important" }}
                    >
                      <Select
                        size="small"
                        value={combineYear}
                        onChange={(e) => {
                          setCombineYear(e.target.value);
                          //fetchEventname(e.target.value);
                        }}
                        sx={{
                          backgroundColor: "#fff",
                          color: "#1d428a",
                          border: "none",
                          fontSize: "0.9rem",
                        }}
                        inputProps={{ "aria-label": "Without label" }}
                        displayEmpty
                        MenuProps={{
                          PaperProps: { style: { maxHeight: 200 } },
                        }}
                      >
                        {combineYearDropdownData.map((elem, i) => {
                          return (
                            <MenuItem key={i} value={elem}>
                              <span
                                title={elem}
                                className={classes.eventTitleWrapper}
                              >
                                {elem}
                              </span>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Push Notification Section */}
            <NotificationPopover user={user} />

            <div>
              <div style={{ display: "flex", flexDirection: "row" }}>
                <IconButton
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                  style={{ paddingRight: "8px" }}
                >
                  <Avatar className={classes.initails}>
                    {user && (
                      <Typography variant="subtitle1">
                        {" "}
                        {Helper.GetNameInitials(
                          `${user.firstName ?? ""} ${user.lastName ?? ""}`
                        )}
                      </Typography>
                    )}
                  </Avatar>
                </IconButton>

                {user.userName !== originalUser.userName && (
                  <IconButton
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={() => {
                      endUserPersonate();
                      navigateToHomeScreen();
                    }}
                    color="inherit"
                    style={{ padding: "0", alignSelf: "flex-start" }}
                    className={classes.clearIcon}
                  >
                    <ClearIcon fontSize="small" color="secondary" />
                  </IconButton>
                )}
                <Hidden smDown>
                  <Typography
                    variant="subtitle2"
                    style={{ alignSelf: "center", fontSize: "1.1rem" }}
                  >
                    {user && `  ${user.firstName ?? ""} ${user.lastName ?? ""}`}
                  </Typography>
                </Hidden>
              </div>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                //  keepMounted
                onClose={handleClose}
                open={open}
                className={classes.mainMenu}
              >
                <List className={classes.list}>
                  <ListItem
                    className={classes.menu}
                    style={{ paddingBottom: "20px" }}
                  >
                    <ListItemAvatar>
                      <Avatar className={classes.avatar}>
                        {user &&
                          Helper.GetNameInitials(
                            `${user.firstName} ${user.lastName}`
                          )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      id="user-name"
                      primary={user && `${user.firstName} ${user.lastName}`}
                    />
                  </ListItem>

                  {user &&
                    user.appRoles &&
                    user.appRoles.includes(Role.Admin) && (
                      <>
                        <Divider></Divider>
                        <MenuItem onClick={handleManageUsersClick}>
                          <ListItemIcon className={classes.itemIcon}>
                            <PeopleAltIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Change Users" />
                        </MenuItem>
                      </>
                    )}

                  {user.userName === originalUser.userName && (
                    <>
                      {/******Menus for All users***********/}
                      {/* <MenuItem>
                    <ListItemIcon className={classes.itemIcon}>
                      <EmailOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Update Email" />
                  </MenuItem>
                  <MenuItem>
                    <ListItemIcon className={classes.itemIcon}>
                      <VpnKeyOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Change Password" />
                  </MenuItem> */}
                    </>
                  )}

                  {user &&
                    Object.keys(user).length > 0 &&
                    user.userName === originalUser.userName && (
                      <>
                        <Divider></Divider>
                        <MenuItem>
                          <ListItemIcon
                            className={classes.itemIcon}
                            onClick={handleManageUsersPreferencesClick}
                          >
                            <ManageAccountsIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Manage User Preferences"
                            onClick={handleManageUsersPreferencesClick}
                          />
                        </MenuItem>
                        <Divider></Divider>
                      </>
                    )}

                  {user.isAdmin && showVideoDashboardScreen && (
                    <>
                      <Divider></Divider>
                      <MenuItem>
                        <ListItemIcon
                          className={classes.itemIcon}
                          onClick={handleRevisedGameAuditClick}
                        >
                          <VideoLibraryIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Video Dashboard"
                          onClick={handleRevisedGameAuditClick}
                        />
                      </MenuItem>
                      <Divider></Divider>
                    </>
                  )}

                  {user.isAdmin && (
                    <>
                      <Divider></Divider>
                      <MenuItem>
                        <ListItemIcon
                          className={classes.itemIcon}
                          onClick={navigateTeamPlayersClick}
                        >
                          <GroupAddIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Manage Team Players"
                          onClick={navigateTeamPlayersClick}
                        />
                      </MenuItem>
                      <Divider></Divider>
                      <MenuItem>
                        <ListItemIcon
                          className={classes.itemIcon}
                          onClick={navigateFiveOnFiveGamesClick}
                        >
                          <GroupAddIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Manage 5 on 5 Games"
                          onClick={navigateFiveOnFiveGamesClick}
                        />
                      </MenuItem>
                      <Divider></Divider>
                    </>
                  )}

                  {/* {user.userName === originalUser.userName && (
                    <>
                      <Divider></Divider>
                      <MenuItem>
                        <ListItemIcon
                          className={classes.itemIcon}
                          onClick={navigateEventCalendarClick}
                        >
                          <CalendarMonthIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Event Calendar"
                          onClick={navigateEventCalendarClick}
                        />
                      </MenuItem>
                      <Divider></Divider>
                    </>
                  )} */}

                  {user.userName === originalUser.userName && (
                    <>
                      <MenuItem>
                        <ListItemIcon
                          className={classes.itemIcon}
                          onClick={handleManageNotification}
                        >
                          <CircleNotificationsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Notifications"
                          onClick={handleManageNotification}
                        />
                      </MenuItem>
                      <Divider></Divider>
                    </>
                  )}

                  {user.userName === originalUser.userName && (
                    <>
                      <MenuItem>
                        <ListItemIcon
                          className={classes.itemIcon}
                          onClick={logout}
                        >
                          <ExitToAppIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Sign Out" onClick={logout} />
                      </MenuItem>
                      <Divider></Divider>
                    </>
                  )}
                </List>
              </Menu>
            </div>
          </Toolbar>

          <AppSidebar
            open={appSideBarOpen}
            onClose={toggleAppSideBar(false)}
            user={user}
            logout={logout}
            userAppRoles={userAppRoles}
            eventId={selectedEventId}
          />
        </AppBar>
      )}
    </>
  );
}
